import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { ApiClient } from '../utils/apiClient';

export default function ParrotConfiguration() {
  const navigate = useNavigate();
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const apiClient = useRef(new ApiClient(getToken)).current;
  const [firstMessage, setFirstMessage] = useState('');
  const [systemPrompt, setSystemPrompt] = useState({
    act: '',
    age: '',
    region: '',
    professionalism: 'professional',
    knowsLocation: false,
  });
  const [temperature, setTemperature] = useState('deterministic');
  const [knowledgeBaseUrl, setKnowledgeBaseUrl] = useState('');
  const [useRag, setUseRag] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState('');
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(true);
  const [playingPreview, setPlayingPreview] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  
  // Authentication and subscription state
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [userSubscription, setUserSubscription] = useState(null);

  // Check authentication and subscription status
  useEffect(() => {
    const checkAuthAndSubscription = async () => {
      if (!isLoaded) return;
      
      if (!isSignedIn) {
        navigate('/sign-in');
        return;
      }

      try {
        const userData = await apiClient.get('/api/auth/me');
        
        // Check if user has an active subscription
        if (!userData.subscription || userData.subscription.status !== 'active') {
          navigate('/plans');
          return;
        }

        setUserSubscription(userData.subscription);
        setAuthLoading(false);
      } catch (error) {
        console.error('Auth check failed:', error);
        setAuthError('Failed to verify subscription status');
        setAuthLoading(false);
      }
    };

    checkAuthAndSubscription();
  }, [isLoaded, isSignedIn, getToken, navigate]);

  // Fetch available voices on component mount (only after auth is verified)
  useEffect(() => {
    if (!authLoading && userSubscription) {
      fetchVoices();
    }
  }, [authLoading, userSubscription]);

  const fetchVoices = async () => {
    try {
      const voicesData = await apiClient.get('/api/voices');
      setVoices(voicesData);
      // Set first voice as default if none selected
      if (voicesData.length > 0 && !selectedVoice) {
        setSelectedVoice(voicesData[0].service_voice_id);
      }
    } catch (error) {
      console.error('Failed to fetch voices:', error);
    } finally {
      setLoadingVoices(false);
    }
  };

  const handleSystemPromptChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSystemPrompt((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const playVoicePreview = async (voiceId, previewFile) => {
    if (playingPreview === voiceId) {
      // Stop current preview
      setPlayingPreview(null);
      return;
    }

    try {
      setPlayingPreview(voiceId);
      // Fetch audio from ASSETS R2 bucket via API
      const audio = new Audio(`/api/voices/preview/${previewFile}`);
      audio.onended = () => setPlayingPreview(null);
      audio.onerror = () => setPlayingPreview(null);
      await audio.play();
    } catch (error) {
      console.error('Failed to play preview:', error);
      setPlayingPreview(null);
    }
  };

  const getTemperatureValue = (preset) => {
    switch (preset) {
      case 'deterministic': return 0.1;
      case 'creative': return 0.7;
      case 'more-creative': return 1.0;
      default: return 0.7;
    }
  };

  const handleSave = async () => {
    try {
      const token = await getToken();
      // Build system prompt from guided inputs
      const systemPromptText = `You are ${systemPrompt.act || 'a helpful assistant'}${systemPrompt.age ? ` in your ${systemPrompt.age}` : ''}${systemPrompt.region ? ` with a ${systemPrompt.region} accent` : ''}. You should be ${systemPrompt.professionalism === 'professional' ? 'professional and formal' : 'casual and easy-going'} in your responses.${systemPrompt.knowsLocation ? ' You are aware of your location and can provide location-specific information.' : ''}`;

      const parrotConfig = {
        name: 'My Parrot', // You might want to add a name field
        first_message: firstMessage,
        system_prompt: systemPromptText,
        temperature: getTemperatureValue(temperature),
        knowledge_base_url: knowledgeBaseUrl || null,
        use_rag: useRag,
        voice_id: selectedVoice,
        model_id: 'eleven_turbo_v2_5'
      };

      await apiClient.post('/api/parrots', parrotConfig);
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 3000);
    } catch (error) {
      console.error('Error saving parrot configuration:', error);
    }
  };

  // Show loading screen while checking authentication
  if (!isLoaded || authLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-gray-300 dark:text-gray-600">Loading parrot configuration...</p>
        </div>
      </div>
    );
  }

  // Show error if authentication failed
  if (authError) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{authError}</p>
          <button
            onClick={() => navigate('/plans')}
            className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 dark:text-white"
          >
            Go to Plans
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 text-gray-200">
      <h1 className="text-3xl font-bold mb-6">Configure Your AI Parrot</h1>
      <div className="space-y-8">
        {/* First Message */}
        <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold text-white dark:text-white">First Message</h2>
          <p className="text-sm text-gray-400 mb-2">
            This will be spoken word-for-word at the beginning of the call.
          </p>
          <textarea
            rows="3"
            className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:text-white"
            value={firstMessage}
            onChange={(e) => setFirstMessage(e.target.value)}
            placeholder="e.g., Hello, you've reached the office of Dr. Smith. How can I help you today?"
          ></textarea>
        </div>

        {/* Voice Selection */}
        <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold text-white dark:text-white">Voice Selection</h2>
          <p className="text-sm text-gray-400 mb-4">
            Choose the voice personality for your parrot.
          </p>
          
          {loadingVoices ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto"></div>
              <p className="mt-2 text-gray-400">Loading voices...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {voices.map((voice) => (
                <div
                  key={voice.service_voice_id}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedVoice === voice.service_voice_id
                      ? 'border-indigo-500 bg-indigo-900/20'
                      : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                  }`}
                  onClick={() => setSelectedVoice(voice.service_voice_id)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-semibold text-white dark:text-white">{voice.parrotmate_name}</h3>
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                        voice.gender === 'Male' ? 'bg-blue-900 text-blue-200' : 'bg-pink-900 text-pink-200'
                      }`}>
                        {voice.gender}
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        playVoicePreview(voice.service_voice_id, voice.preview_audio_file);
                      }}
                      className={`p-2 rounded-full transition-colors ${
                        playingPreview === voice.service_voice_id
                          ? 'bg-red-600 hover:bg-red-700'
                          : 'bg-indigo-600 hover:bg-indigo-700'
                      }`}
                      title={playingPreview === voice.service_voice_id ? 'Stop Preview' : 'Play Preview'}
                    >
                      {playingPreview === voice.service_voice_id ? (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <p className="text-sm text-gray-300 mb-2 dark:text-gray-600">{voice.short_description}</p>
                  <p className="text-xs text-gray-400">{voice.long_description}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* System Prompt */}
        <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold text-white dark:text-white">System Prompt</h2>
          <p className="text-sm text-gray-400 mb-4">
            Guide your parrot's personality and behavior.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 dark:text-gray-600">How should the parrot act?</label>
              <input
                type="text"
                name="act"
                value={systemPrompt.act}
                onChange={handleSystemPromptChange}
                className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm dark:text-white"
                placeholder="e.g., a helpful medical assistant"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 dark:text-gray-600">Age</label>
              <input
                type="text"
                name="age"
                value={systemPrompt.age}
                onChange={handleSystemPromptChange}
                className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm dark:text-white"
                placeholder="e.g., 30s"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 dark:text-gray-600">Region / Accent</label>
              <input
                type="text"
                name="region"
                value={systemPrompt.region}
                onChange={handleSystemPromptChange}
                className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm dark:text-white"
                placeholder="e.g., American, British"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 dark:text-gray-600">Tone</label>
              <select
                name="professionalism"
                value={systemPrompt.professionalism}
                onChange={handleSystemPromptChange}
                className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm dark:text-white"
              >
                <option value="professional">Professional</option>
                <option value="easy-going">Easy-going</option>
              </select>
            </div>
            <div className="flex items-center">
              <input
                id="knows-location"
                name="knowsLocation"
                type="checkbox"
                checked={systemPrompt.knowsLocation}
                onChange={handleSystemPromptChange}
                className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-indigo-600 focus:ring-indigo-500"
              />
              <label htmlFor="knows-location" className="ml-2 block text-sm text-gray-200">
                Should the parrot know its location?
              </label>
            </div>
          </div>
        </div>

        {/* Temperature */}
        <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold text-white dark:text-white">Temperature</h2>
          <p className="text-sm text-gray-400 mb-2">
            Controls the creativity or randomness of the responses.
          </p>
          <div className="flex gap-4">
            <button onClick={() => setTemperature('deterministic')} className={`p-2 rounded transition ${temperature === 'deterministic' ? 'bg-indigo-600 text-white' : 'bg-gray-700 hover:bg-gray-600'}`}>Deterministic</button>
            <button onClick={() => setTemperature('creative')} className={`p-2 rounded transition ${temperature === 'creative' ? 'bg-indigo-600 text-white' : 'bg-gray-700 hover:bg-gray-600'}`}>Creative</button>
            <button onClick={() => setTemperature('more-creative')} className={`p-2 rounded transition ${temperature === 'more-creative' ? 'bg-indigo-600 text-white' : 'bg-gray-700 hover:bg-gray-600'}`}>More Creative</button>
          </div>
        </div>

        {/* Knowledge Base */}
        <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold text-white dark:text-white">Parrot Knowledge Base</h2>
          <div className="space-y-4 mt-2">
            <div>
              <label className="block text-sm font-medium text-gray-300 dark:text-gray-600">URL for knowledge</label>
              <input
                type="url"
                value={knowledgeBaseUrl}
                onChange={(e) => setKnowledgeBaseUrl(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm dark:text-white"
                placeholder="https://example.com/faq"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 dark:text-gray-600">Add document</label>
              <input type="file" className="mt-1 block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-500 file:text-white hover:file:bg-indigo-600 dark:text-white"/>
            </div>
            <div className="flex items-center">
              <input
                id="use-rag"
                type="checkbox"
                checked={useRag}
                onChange={(e) => setUseRag(e.target.checked)}
                className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-indigo-600 focus:ring-indigo-500"
              />
              <label htmlFor="use-rag" className="ml-2 block text-sm text-gray-200">
                Use RAG (Retrieval-Augmented Generation)
              </label>
            </div>
            <p className="text-xs text-gray-500">
              RAG increases the parrot's maximum Knowledge Base size. The parrot will have access to relevant pieces of attached Knowledge Base during answer generation.
            </p>
          </div>
        </div>

        <button onClick={handleSave} className="p-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded transition dark:text-white">
          Save Configuration
        </button>
      </div>
      {isSaved && (
        <div className="mt-4 p-4 bg-green-900 text-green-200 rounded">
          <p>Parrot configuration saved successfully!</p>
        </div>
      )}
    </div>
  );
}
